import { TimeSpan } from "@sparkware/app-utils";
import { ServiceBase } from "../ServiceBase";
import { IServiceFactoryExtended } from "../service-factory-extended.interface";
import { GameInfo } from "../games/game-info";
import { IdleRecommendationComponent } from "../../components/idle-recommendation/idle-recommendation.component";
import { makeObservable, observable } from "mobx";
import { CyClass } from "../../../common/cy-classes";
import {LocalStorageKeys} from "../storage/local-storage-keys";
import {IToastHandler} from "../toaster/toaster.service.interface";
import {NullableNumber} from "../../../common/types/nullable-types";
import {FeatureToggleStatusEnum} from "../ab-testing/feature-toggle.view-model.interface";


const ACTIVITY_DETECTION_INTERVAL: TimeSpan = TimeSpan.fromSeconds(1);

interface IIdleRecommendationsStorageData {
    noOfAppearancesInSession: number;
}

export class IdleRecommendationsService extends ServiceBase {

    private readonly _idleDetectionInterval: TimeSpan;
    private readonly _maxAppearancesPerSession: number;
    private readonly _toastTimeout: TimeSpan;

    private _idleDetectionTimerRef: ReturnType<typeof setInterval>;
    private _userActivityDetectorRef: any;

    private _recommendedGameType: NullableNumber = null;
    private _currentToastHandler: IToastHandler | null = null;

    @observable.ref
    private _isThereRunningApps = true;

    constructor(services: IServiceFactoryExtended) {
        super(services);

        makeObservable(this);

        this._idleDetectionInterval = TimeSpan.parse(this.services.configuration.servicesConfigs.IdleRecommendations.idleDetectionInterval);
        this._maxAppearancesPerSession = this.services.configuration.servicesConfigs.IdleRecommendations.maxAppearancesPerSession;
        this._toastTimeout = TimeSpan.parse(this.services.configuration.servicesConfigs.IdleRecommendations.displayInterval);

        this._subscribe();
    }


    private get isEnabled(): boolean {
        return this.services.configuration.servicesConfigs.IdleRecommendations.featureToggle === FeatureToggleStatusEnum.Enabled;
    }


    private get numberOfAppearancesInSession(): number {
        return this.services.localStorage.getJson<IIdleRecommendationsStorageData>(LocalStorageKeys.idleRecommendationsData)?.noOfAppearancesInSession ?? 0;
    }

    private set numberOfAppearancesInSession(value: number) {
        const storageData: IIdleRecommendationsStorageData = {
            noOfAppearancesInSession: value
        }
        this.services.localStorage.setJson(LocalStorageKeys.idleRecommendationsData, storageData);
    }

    private _subscribe(): void {
        if(!this.isEnabled) {
            return;
        }

        this.services.sdk.events.onGameClose(this._onGameClosed.bind(this));
        this.services.sdk.events.onGameOpen(this._onGameOpened.bind(this));

        this.services.appLauncher.then(appLauncher => {
            this.reaction(() => appLauncher.isThereRunningApps,
                (isThereRunningApps) => {
                    this._isThereRunningApps = isThereRunningApps;
                },
                {
                    fireImmediately: true
                })
        });

        this.reaction(() => this.services.user.isAuthorized,
            (isAuthorized) => {
                if(isAuthorized) {
                    this._startUserIdleDetection();
                    this._startUserActivityDetection();
                } else {
                    this._stopUserIdleDetection();
                    this._stopUserActivityDetection();
                    this._closeCurrentToast();
                    this.numberOfAppearancesInSession = 0;
                }

            }, {
                fireImmediately: true
            });
    }

    private _startUserIdleDetection() {
        this._idleDetectionTimerRef = setInterval(async () => {
            await this._showIdleRecommendationToaster();
        }, this._idleDetectionInterval.totalMilliseconds);
    }

    private _stopUserIdleDetection() {
        if (this._idleDetectionTimerRef) {
            clearInterval(this._idleDetectionTimerRef)
            this._idleDetectionTimerRef = null;
        }
    }

    private _restartUserIdleDetection = () => {
        this._stopUserIdleDetection();
        this._startUserIdleDetection();
    }

    private _startUserActivityDetection() {
        this._userActivityDetectorRef = this.services.sdk.createUserActivityDetactor({
            userActivityDetectionInterval: ACTIVITY_DETECTION_INTERVAL,
            onUserActivityCallback: this._restartUserIdleDetection.bind(this),
        })
    }

    private _stopUserActivityDetection(): void {
        this._userActivityDetectorRef?.dispose();
        this._userActivityDetectorRef = null;
    }

    private _onGameOpened = () => {
        if (this._currentToastHandler) {
            this._onCloseToastAnalytics = this._sendAnalyticsAnotherGameOpenWhileToastIsVisible;
            this._closeCurrentToast();
        }
    }

    private _onGameClosed = () => {
        this._restartUserIdleDetection();
    }

    // We have two different types of close analytics: one for clicking the idle toast X button and one for launching a game (NOT the one in idle) while the idle is active.
    private _defaultOnCloseAnalytics = () => {
        this._sendAnalytics({ action: 'click_close_button', label: this._recommendedGameType.toString() });
    };

    private _sendAnalyticsAnotherGameOpenWhileToastIsVisible = () => {
        this._sendAnalytics({ action: 'launched_other_game', label: this._recommendedGameType.toString() });
    };

    private _sendAnalyticsRecommendedGameOpen = () => {
        this._sendAnalytics({ action: 'click_play', label: this._recommendedGameType.toString() });
    };

    private _onCloseToastAnalytics = this._defaultOnCloseAnalytics;

    private _onToastClosed() {
        this._onCloseToastAnalytics();
        this._restartUserIdleDetection();
        this._currentToastHandler = null;
    }

    private async _showIdleRecommendationToaster() {
        if (!this.isEnabled) {
            return;
        }

        if (this._currentToastHandler
            || Boolean((await this.services.gameRunner)?.currentRunningGame)
            || this.services.overlay.isOpen
            || this.services.dialog.hasOpenDialogs) {

            return;
        }

        if (this.services.arenas.getCurrentArena()?.preventIdleRecommendations) {
            return;
        }

        if (this.numberOfAppearancesInSession >= this._maxAppearancesPerSession) {
            return;
        }

        const gameTypeForCorrelation = await this._getGameTypeForCorrelation();

        if(!gameTypeForCorrelation) {
            return;
        }

        this._recommendedGameType = await this._getCorrelatedGame(gameTypeForCorrelation);

        if(!this._recommendedGameType) {
            return;
        }

        const gameInfo = await this.services.games.getUnrestrictedGameInfo(this._recommendedGameType);
        if(!gameInfo) {
            return;
        }

        await this._showToaster(gameInfo)
    }

    private _closeCurrentToast(): void {
        this._currentToastHandler?.close();
    }

    private async _showToaster(gameInfo: GameInfo): Promise<void> {
        this._onCloseToastAnalytics = this._defaultOnCloseAnalytics;

        this._currentToastHandler = this.services.toaster.showCustomToast({
            id: "idle_recommendation_toast",
            toastOptions: {
                timeout: TimeSpan.fromSeconds(this._toastTimeout.totalSeconds),
                hideProgressBar: true,
                className: CyClass('cy-idle-recommendation-toast')
            },
            renderContent: (handler) => {
                return <IdleRecommendationComponent key={'IdleRecommendation'}
                                                    gameType={gameInfo.gameType}
                                                    onPlayRecommendedGame={() => {
                                                        this._onCloseToastAnalytics = this._sendAnalyticsRecommendedGameOpen;
                                                        this._closeCurrentToast();
                                                        gameInfo.open({isRealMode: true});
                                                    }}/>
            },
            onToastShown: () => {
                this.numberOfAppearancesInSession++;
                this._sendAnalytics({ action: 'view', label: this._recommendedGameType?.toString(), value: this.numberOfAppearancesInSession});
            },
            onToastClosed: () => {
                this._onToastClosed();

            },
            displayCondition: () => !this._isThereRunningApps,
        });

    }

    private async _getGameTypeForCorrelation(): Promise<NullableNumber> {
        const recentlyGamesPlayed = this.services.games.recentlyPlayedGames.gameTypes
        if (recentlyGamesPlayed.length > 0) {
            return recentlyGamesPlayed[0];
        }

        const aiGameTypesArray = this.services.aiNewEngine.aiNewRecommendedGames?.gameTypes ?? [];

        if(aiGameTypesArray.length === 0) {
            return null;
        }

        return aiGameTypesArray[this._getRandomIndex(aiGameTypesArray.length)];
    }

    private async _getCorrelatedGame(gameType: number): Promise<NullableNumber> {
        const correlatedGames = await this.services.games.getCorrelatedGames(gameType, 6);
        if(correlatedGames.length === 0) {
            return null;
        }


        return correlatedGames[this._getRandomIndex(correlatedGames.length)];
    }


    private _sendAnalytics(analyticsData: {action: string; label: string, value?: string | number}): void {
        this.services.analytics.sendOrbitAIFeatureEvent(
            'idleRecommendations', // Category
            analyticsData?.action,
            analyticsData?.label,
            analyticsData?.value
        );
    }

    private _getRandomIndex(arrayLength: number): number {
        const randomIndex = Math.floor(Math.random() * arrayLength);
        return Math.min(randomIndex, arrayLength - 1)
    }
}